import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useRealtimeHealth } from '../hooks/useRealtimeHealth';
import { useAuth } from '../utils/useAuth';

const OFFLINE_WARNING_MS = 30_000;

const STATUS_COLORS: Record<string, { dot: string; text: string; bg: string; border: string }> = {
  CONNECTED:    { dot: '#2d7a4f', text: '#2d7a4f', bg: 'rgba(5,166,90,0.1)',   border: 'rgba(5,166,90,0.12)' },
  STALE:        { dot: '#a06020', text: '#a06020', bg: 'rgba(217,179,14,0.1)',  border: 'rgba(217,179,14,0.12)' },
  DISCONNECTED: { dot: '#b94040', text: '#b94040', bg: 'rgba(219,55,55,0.1)',   border: 'rgba(219,55,55,0.12)' },
  RECONNECTING: { dot: '#e8930a', text: '#e8930a', bg: 'rgba(232,147,10,0.1)', border: 'rgba(232,147,10,0.12)' },
};

const LiveEventsHealthIndicator: React.FC = () => {
  const { status, connectedUsers, reconnect, lastEventTime, disconnectedAt } = useRealtimeHealth();
  const { appUser, user: authUser } = useAuth();
  const [showWarningBanner, setShowWarningBanner] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);

  // Show warning banner if offline for >30 seconds
  useEffect(() => {
    if (!disconnectedAt) { setShowWarningBanner(false); return; }
    const remaining = OFFLINE_WARNING_MS - (Date.now() - disconnectedAt.getTime());
    if (remaining <= 0) { setShowWarningBanner(true); return; }
    const timeout = setTimeout(() => setShowWarningBanner(true), remaining);
    return () => clearTimeout(timeout);
  }, [disconnectedAt]);

  // Close popover on outside click
  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen]);

  const colors = STATUS_COLORS[status] || STATUS_COLORS.DISCONNECTED;

  const statusText = useMemo(() => {
    switch (status) {
      case 'CONNECTED': return 'Online';
      case 'DISCONNECTED': return 'Offline';
      case 'RECONNECTING': return 'Reconnecting...';
      case 'STALE': return 'Connection Stale';
      default: return 'Unknown';
    }
  }, [status]);

  const currentUserId = appUser?.id || authUser?.id;

  const { uniqueUsers, myDeviceCount } = useMemo(() => {
    const deviceCounts = new Map<string | number, number>();
    const userMap = new Map<string | number, typeof connectedUsers[0]>();
    connectedUsers.forEach(u => {
      const key = u.user_id ?? u.presence_ref;
      deviceCounts.set(key, (deviceCounts.get(key) || 0) + 1);
      userMap.set(key, u);
    });
    const myCount = currentUserId ? (deviceCounts.get(currentUserId) || 1) : 1;
    return { uniqueUsers: Array.from(userMap.values()), myDeviceCount: myCount };
  }, [connectedUsers, currentUserId]);

  return (
    <>
      {showWarningBanner && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
          padding: '8px 16px',
          background: 'var(--red)', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
          fontSize: '13px', fontWeight: 500,
        }}>
          <span>Real-time connection lost. Data may be stale.</span>
          <button className="sky-btn sky-btn-ghost" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)' }} onClick={reconnect}>Reconnect</button>
        </div>
      )}
      <div ref={popRef} style={{ position: 'relative' }}>
        <div
          onClick={() => setPopoverOpen(!popoverOpen)}
          style={{
            display: 'flex', alignItems: 'center', cursor: 'pointer',
            padding: '5px 10px', borderRadius: '16px',
            background: colors.bg, border: `1px solid ${colors.border}`,
            transition: 'all 0.2s',
          }}
        >
          <div style={{
            width: 6, height: 6, borderRadius: '50%',
            background: colors.dot, marginRight: 6,
            boxShadow: status === 'CONNECTED' ? `0 0 6px ${colors.dot}` : 'none',
          }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>Live</span>
        </div>

        {popoverOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 50,
            minWidth: 240, background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: 'var(--shadow-lg)', padding: 14,
            animation: 'sky-fade-in 0.12s ease',
          }}>
            <h4 style={{ margin: '0 0 12px', fontWeight: 600, fontSize: 13 }}>Real-Time Status</h4>
            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.dot, boxShadow: `0 0 4px ${colors.dot}` }} />
              <strong style={{ fontSize: 12.5 }}>{statusText}</strong>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>
              <div>Last event: {lastEventTime ? lastEventTime.toLocaleTimeString() : 'None'}</div>
              {myDeviceCount > 1 && <div>You are logged in on {myDeviceCount - 1} other {myDeviceCount - 1 === 1 ? 'device' : 'devices'}</div>}
            </div>
            <div style={{ borderTop: '1px solid var(--border)', margin: '8px 0', paddingTop: 8 }}>
              <h5 style={{ margin: '0 0 8px', fontSize: 10.5, textTransform: 'uppercase', color: 'var(--text-3)', letterSpacing: 0.5, fontWeight: 600 }}>
                Connected Users ({uniqueUsers.length})
              </h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 200, overflowY: 'auto' }}>
                {uniqueUsers.map((user, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-2)' }}>
                    <div style={{
                      width: 20, height: 20, borderRadius: '50%', background: 'var(--bg)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 600, color: 'var(--text-3)', flexShrink: 0,
                    }}>
                      {user.name ? user.name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <span>
                      {user.name || user.email || 'Anonymous'}
                      {(user.user_id === appUser?.id || user.user_id === authUser?.id) && <span style={{ color: 'var(--text-3)', marginLeft: 4 }}>(You)</span>}
                    </span>
                  </div>
                ))}
                {uniqueUsers.length === 0 && (
                  <span style={{ color: 'var(--text-3)', fontSize: 12, fontStyle: 'italic' }}>No active users</span>
                )}
              </div>
            </div>
            {status !== 'CONNECTED' && (
              <button className="sky-btn sky-btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={reconnect}>
                Reconnect Now
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default LiveEventsHealthIndicator;
